import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useLineEditing } from '@/apps/latest/components/organisms/PostRecordingView/hooks/useLineEditing'
import { useToast } from '@/shared/contexts/ToastContext'
import { type SoapContentItem } from '@/shared/types/yakurekiSoap'

import {
  type CarePlan,
  type CarePlans,
  createCarePlan,
  getCarePlans,
  patchCarePlan,
  reviewCarePlan,
} from './carePlanApi'

// 計画書の見出し (画面と紙の並び)。back の home_care/services/care_plan.py の CARE_PLAN_KEYS と 1:1。
// 並びはアイセイ P-CUBEn 取り込み用の計画書の様式。visit_record は見直しの版だけに入る。
export const CARE_PLAN_FIELDS: { key: string; label: string }[] = [
  { key: 'visit_record', label: '前の版からの訪問' },
  { key: 'visit_frequency', label: '訪問頻度' },
  { key: 'collaborator_info', label: '連携機関からの情報' },
  { key: 'requested_guidance', label: '指導依頼内容' },
  { key: 'patient_profile', label: '患者さま基本情報' },
  { key: 'guidance_points', label: '指導要点' },
  { key: 'management_goals', label: '訪問服薬管理目標' },
]

// 前の版からの訪問は見直しの版だけ。最初の版では見出しごと出さない
export const visibleCarePlanFields = (plan: CarePlan) =>
  CARE_PLAN_FIELDS.filter(
    ({ key }) => key !== 'visit_record' || (plan.fields[key] ?? []).length > 0
  )

export const carePlansKey = (patientId: string) =>
  ['homecare', 'plans', patientId] as const

// 書いている途中はこの間隔で取り直す (着地すれば止まる)
const GENERATING_POLL_MS = 3000

export const useCarePlans = (patientId: string | null) =>
  useQuery({
    queryKey: carePlansKey(patientId ?? ''),
    queryFn: () => getCarePlans(patientId!),
    enabled: patientId !== null,
    refetchInterval: (query) =>
      query.state.data?.plans[0]?.is_generating ? GENERATING_POLL_MS : false,
  })

// 版を作ると、患者のページの計画書の行も古くなる
const useInvalidateHomecare = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['homecare'] })
}

export const useCreateCarePlan = (patientId: string) => {
  const invalidate = useInvalidateHomecare()
  const { showToast } = useToast()
  return useMutation({
    mutationFn: (memo: string) => createCarePlan(patientId, memo),
    onSuccess: () => void invalidate(),
    onError: () => showToast('計画書を作れませんでした', 'error'),
  })
}

export const useReviewCarePlan = (patientId: string) => {
  const invalidate = useInvalidateHomecare()
  const { showToast } = useToast()
  return useMutation({
    mutationFn: () => reviewCarePlan(patientId),
    onSuccess: () => void invalidate(),
    onError: () => showToast('見直しの版を作れませんでした', 'error'),
  })
}

const replacePlan = (data: CarePlans | undefined, next: CarePlan) =>
  data
    ? {
        ...data,
        plans: data.plans.map((plan) => (plan.id === next.id ? next : plan)),
      }
    : data

// 予定日の差し替え。過ぎた日を動かそうとすると back が断る (画面でも押せなくしてある)
export const useScheduledDates = (patientId: string, plan: CarePlan) => {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateHomecare()
  const { showToast } = useToast()
  return useMutation({
    scope: { id: `care-plan:${plan.id}` },
    mutationFn: (dates: string[]) =>
      patchCarePlan(plan.id, { scheduled_dates: dates }),
    onSuccess: (next) => {
      queryClient.setQueryData<CarePlans>(carePlansKey(patientId), (data) =>
        replacePlan(data, next)
      )
      void invalidate()
    },
    // 過ぎた日を動かそうとしたときは back の文言 (今日より前の予定日は変えられません。) を出す
    onError: (error) => {
      const serverMessage = (
        error as { response?: { data?: { error?: string } } }
      ).response?.data?.error
      showToast(serverMessage ?? '予定日を保存できませんでした', 'error')
    },
  })
}

// 計画書の文の 1 行編集・行追加・採否。報告書 (useHomeVisitReportEditing) と同じ操作感と同じ契約
// (送るのは触ったキーだけ・楽観更新・失敗で戻す・同じ版への保存は直列)。
export const useCarePlanEditing = (patientId: string, plan: CarePlan) => {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const key = useMemo(() => carePlansKey(patientId), [patientId])

  const { mutateAsync } = useMutation({
    scope: { id: `care-plan:${plan.id}` },
    mutationFn: async ({
      field,
      nextItems,
    }: {
      field: string
      nextItems: SoapContentItem[]
    }) => patchCarePlan(plan.id, { fields: { [field]: nextItems } }),
    onMutate: async ({ field, nextItems }) => {
      // 書いている途中の取り直しが楽観状態を巻き戻さないように止めてから書く
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<CarePlans>(key)
      const current = previous?.plans.find((p) => p.id === plan.id)
      if (current) {
        queryClient.setQueryData<CarePlans>(key, (data) =>
          replacePlan(data, {
            ...current,
            fields: { ...current.fields, [field]: nextItems },
          })
        )
      }
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      showToast('保存に失敗しました', 'error')
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  })

  // items は cache 起点で引く (連打の 2 回目が render 時点の古い値を見ないように)
  const itemsOf = useCallback(
    (field: string): SoapContentItem[] => {
      const cached = queryClient
        .getQueryData<CarePlans>(key)
        ?.plans.find((p) => p.id === plan.id)
      return (cached ?? plan).fields[field] ?? []
    },
    [queryClient, key, plan]
  )

  const commit = useCallback(
    async (field: string, nextItems: SoapContentItem[]) => {
      try {
        await mutateAsync({ field, nextItems })
        return true
      } catch {
        return false
      }
    },
    [mutateAsync]
  )

  const line = useLineEditing<string>({ itemsOf, commit })

  const toggleSelect = useCallback(
    (field: string, index: number) => {
      const items = [...itemsOf(field)]
      const item = items[index]
      if (!item) return
      items[index] = { ...item, is_selected: !(item.is_selected ?? true) }
      void commit(field, items)
    },
    [itemsOf, commit]
  )

  return { ...line, toggleSelect }
}
